/**
 * Shared types for @matchday/juice.
 *
 * Tier vocabulary matches the `ceremony_played` / `perf_vitals_reported`
 * contracts (packages/contracts/src/events.ts): "FULL" | "TRIM" | "LITE".
 */

/** Device intensity tier — FIRST BLOOD §8 / JUICE-FLUTTER §7 ladder. */
export type Tier = "FULL" | "TRIM" | "LITE";

export const TIER_ORDER: readonly Tier[] = ["FULL", "TRIM", "LITE"];

/** Returns the lower (more degraded) of two tiers. */
export function minTier(a: Tier, b: Tier): Tier {
  return TIER_ORDER.indexOf(a) >= TIER_ORDER.indexOf(b) ? a : b;
}

/**
 * Injectable clock — NO module in this package may touch real timers directly.
 * Production uses `realClock` (performance.now + setTimeout); tests use
 * `ManualClock` (src/ceremonies/clock.ts).
 */
export interface JuiceClock {
  now(): number;
  setTimeout(cb: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/**
 * Dev-mode detection per the JUICER contract:
 * `import.meta.env.DEV || process.env.NODE_ENV !== 'production'`.
 * Guarded so it typechecks and runs in both Vite and Node contexts.
 */
export function isDevEnv(): boolean {
  const meta = import.meta as unknown as { env?: { DEV?: boolean } };
  if (meta.env && typeof meta.env.DEV === "boolean") {
    if (meta.env.DEV) return true;
    // Vite says production — still honour an explicit NODE_ENV dev override.
  }
  const proc = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  if (proc?.env) return proc.env.NODE_ENV !== "production";
  if (meta.env && typeof meta.env.DEV === "boolean") return meta.env.DEV;
  return false;
}
